class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);

    // === BUGFIX 1: Auto-close other open desktop filter <details> when one opens,
    //     and close all when clicking outside the filter bar.
    if (!FacetFiltersForm._clickListenerBound) {
      document.addEventListener('click', (event) => {
        const facetsWrapper = document.getElementById('FacetsWrapperDesktop');
        if (!facetsWrapper) return;

        const clickedSummary = event.target.closest('.facets__disclosure > summary');

        if (clickedSummary) {
          const clickedDetails = clickedSummary.parentElement;
          document.querySelectorAll('.facets__disclosure[open]').forEach((d) => {
            if (d !== clickedDetails) d.removeAttribute('open');
          });
          return;
        }

        // Click outside any open horizontal filter -> close all
        const insideOpenDetails = event.target.closest('.facets__disclosure[open]');
        if (!insideOpenDetails) {
          document.querySelectorAll('.facets__disclosure[open]').forEach((d) => {
            d.removeAttribute('open');
          });
        }
      });
      FacetFiltersForm._clickListenerBound = true;
    }

    // === BUGFIX 2: Mobile filter button — make sure the .menu-opening class
    //     gets toggled in sync with the <details> open state, so the drawer
    //     actually slides into view even if menu-drawer.js fails to bind.
    if (!FacetFiltersForm._toggleListenerBound) {
      document.addEventListener('toggle', (event) => {
        const details = event.target;
        if (!details || !details.classList) return;
        if (details.matches && details.matches('.mobile-facets__disclosure')) {
          if (details.open) {
            details.classList.add('menu-opening');
            document.body.classList.add('overflow-hidden', 'overflow-hidden-mobile');
          } else {
            details.classList.remove('menu-opening');
            document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile');
          }
        }
      }, true);
      FacetFiltersForm._toggleListenerBound = true;
    }

    // === BUGFIX 4: Inject CSS for close button + a visible close (×) button into each .facets__header.
    FacetFiltersForm.injectCloseButtonStyles();
    FacetFiltersForm.injectCloseButtons();
  }

  static injectCloseButtonStyles() {
    if (document.getElementById('facets-close-x-styles')) return;
    const style = document.createElement('style');
    style.id = 'facets-close-x-styles';
    style.textContent = `
      .facets__header { align-items: center; }
      .facets__close-x {
        appearance: none;
        background: transparent;
        border: 0;
        cursor: pointer;
        font-size: 2.4rem;
        line-height: 1;
        padding: 0.2rem 0.9rem 0.4rem;
        margin-left: 1rem;
        color: rgb(var(--color-foreground));
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.15s ease;
        flex-shrink: 0;
      }
      .facets__close-x:hover,
      .facets__close-x:focus-visible {
        background-color: rgba(var(--color-foreground), 0.08);
        outline: none;
      }
    `;
    document.head.appendChild(style);
  }

  static injectCloseButtons() {
    document.querySelectorAll('.facets__header').forEach((header) => {
      if (header.querySelector('.facets__close-x')) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'facets__close-x';
      btn.setAttribute('aria-label', 'Close filter');
      btn.innerHTML = '&times;';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const details = btn.closest('details');
        if (details) details.removeAttribute('open');
      });
      header.appendChild(btn);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      const collection = gridContainer.querySelector('.collection');
      if (collection) collection.classList.add('loading');
    }
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    // === BUGFIX 3: Abort previous in-flight request to prevent race conditions
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }
    FacetFiltersForm.abortController = new AbortController();
    const signal = FacetFiltersForm.abortController.signal;

    fetch(url, { signal })
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.applyRender(html, event);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.error('[facets] fetch failed:', err);
        FacetFiltersForm.removeLoadingState();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.applyRender(html, event);
  }

  // === BUGFIX 1+3: Centralised render that wraps each step in try/catch
  //     and ALWAYS clears the loading state, so the spinner overlay can
  //     never get stuck blocking clicks.
  static applyRender(html, event) {
    try { FacetFiltersForm.renderFilters(html, event); } catch (e) { console.error('[facets] renderFilters:', e); }
    try { FacetFiltersForm.renderProductGridContainer(html); } catch (e) { console.error('[facets] renderProductGridContainer:', e); }
    try { FacetFiltersForm.renderProductCount(html); } catch (e) { console.error('[facets] renderProductCount:', e); }
    FacetFiltersForm.removeLoadingState();
    FacetFiltersForm.injectCloseButtons();
  }

  static removeLoadingState() {
    const grid = document.getElementById('ProductGridContainer');
    if (grid) {
      const collection = grid.querySelector('.collection');
      if (collection) collection.classList.remove('loading');
    }
    const countContainer = document.getElementById('ProductCount');
    if (countContainer) countContainer.classList.remove('loading');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
  }

  static renderProductGridContainer(html) {
    const target = document.getElementById('ProductGridContainer');
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (target && source) target.innerHTML = source.innerHTML;
  }

  static renderProductCount(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    if (!source) return;
    const count = source.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target) {
      const targetFilter = event.target.closest('.js-filter');
      if (targetFilter) FacetFiltersForm.renderCounts(countsToRender, targetFilter);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = html.querySelector(selector).innerHTML;
    });

    // === BUGFIX 2+3: Defensive null check + only call bindEvents if available
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const drawer = mobileForm.closest('menu-drawer');
      if (drawer && typeof drawer.bindEvents === 'function') {
        try { drawer.bindEvents(); } catch (e) { console.error('[facets] bindEvents:', e); }
      }
    }
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.abortController = null;
FacetFiltersForm._clickListenerBound = false;
FacetFiltersForm._toggleListenerBound = false;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Inject close buttons after DOM is ready, in case the form was defined before .facets__header was painted.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    FacetFiltersForm.injectCloseButtonStyles();
    FacetFiltersForm.injectCloseButtons();
  });
} else {
  FacetFiltersForm.injectCloseButtonStyles();
  FacetFiltersForm.injectCloseButtons();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
